/* eslint-disable */
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box } from "lucide-react";
import aiPackaging from "@/Services/aiPackaging.services";
import { modalActions } from "@/Actions";
import BatchPerformanceModalContent from "@/Components/Modals/BatchPerformanceModal/BatchPerformanceModalContent";
import moment from "moment";
import { useAuth } from "@/Components/Auth/Login/auth";
import { filterBatchesBySource, DEFAULT_BATCH_SOURCE } from "@/Components/Modals/BatchPerformanceModal/batchSource";

const BatchPerformanceHandler = (props) => {
    const {
        isActive,
        filters,
        onBatchSelect,
        onCloseModal,
        parentSelectedBatches,
        onDateSelect,
        overrideDateRange,
        shiftId,
        onShiftChange,
        isSingleSelect = false,
        isSingleDate = false,
        maxDateRangeDays = null,
        // Opt-in "Select All batches" checkbox in the modal (multi-select only).
        activeSelectAllOption = false,
    } = props;
    const { appConfig = {} } = useAuth();
    const customerId = appConfig?.customerId;
    const dispatch = useDispatch();
    const [selectedBatches, setSelectedBatches] = useState([]);
    const [currentBatches, setCurrentBatches] = useState([]);
    const [lastFilters, setLastFilters] = useState(filters);

    // Planned / Adhoc / All split, filtered here (not inside the modal) so the header
    // count, the visible list and Select All all read the same set of batches.
    const [batchSource, setBatchSource] = useState(DEFAULT_BATCH_SOURCE);
    const visibleBatches = filterBatchesBySource(currentBatches, batchSource);

    // Switching tabs hides batches. Anything already picked but no longer on screen is
    // dropped, so "View Details" can never submit a batch the user cannot see.
    const handleBatchSourceChange = (nextSource) => {
        setBatchSource(nextSource);
        const stillVisible = filterBatchesBySource(currentBatches, nextSource);
        setSelectedBatches((prev) => prev.filter(
            (b) => stillVisible.some((vb) => vb.grnItemBatchId === b.grnItemBatchId)
        ));
    };

    useEffect(() => {
        setSelectedBatches(parentSelectedBatches || []);
    }, [parentSelectedBatches]);

    useEffect(() => {
        if (isActive && parentSelectedBatches?.length === 0) {
            handleFetchAndOpen();
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive) {
            dispatchModal(visibleBatches, lastFilters);
        } else if (isActive && visibleBatches.length === 0) {
            dispatchModal(visibleBatches, lastFilters);
        }
    }, [isActive, currentBatches, lastFilters, selectedBatches, batchSource]);

    const handleViewDetails = () => {
        if (onBatchSelect) {
            const startDate = lastFilters?.from || lastFilters?.dateRange?.from;
            const endDate = lastFilters?.to || lastFilters?.dateRange?.to;
            const dateOverride = (startDate && endDate) ? { from: moment(startDate).toDate(), to: moment(endDate).toDate() } : null;

            if (onDateSelect && dateOverride) {
                onDateSelect(dateOverride.from, dateOverride.to);
            }
            onBatchSelect(selectedBatches, dateOverride);
            onCloseModal(true, selectedBatches, dateOverride);
        }
    };

    const handleBatchSelect = (batch) => {
        const effectiveIsSingleSelect = isSingleSelect;
        if (effectiveIsSingleSelect) {
            if (selectedBatches.find((b) => b?.grnItemBatchId === batch?.grnItemBatchId)) {
                setSelectedBatches([]);
            } else {
                setSelectedBatches([batch]);
            }
            return;
        }

        if (selectedBatches.find((b) => b?.grnItemBatchId === batch?.grnItemBatchId)) {
            setSelectedBatches(selectedBatches.filter((b) => b?.grnItemBatchId !== batch?.grnItemBatchId));
        } else {
            setSelectedBatches([...selectedBatches, batch]);
        }
    };

    // Select or deselect ALL batches for a product (multi-select only)
    const handleProductBatchesSelect = (productBatches, allAlreadySelected) => {
        const effectiveIsSingleSelect = isSingleSelect;
        if (effectiveIsSingleSelect) return;

        if (allAlreadySelected) {
            // Deselect all batches of this product
            setSelectedBatches((prev) =>
                prev.filter((b) => !productBatches.find((pb) => pb.grnItemBatchId === b.grnItemBatchId))
            );
        } else {
            // Add any batches that aren't already selected
            setSelectedBatches((prev) => {
                const newBatches = productBatches.filter(
                    (pb) => !prev.find((b) => b.grnItemBatchId === pb.grnItemBatchId)
                );
                return [...prev, ...newBatches];
            });
        }
    };

    const dispatchModal = (batches, activeFilters) => {
        const ModalHeader = () => (
            <div className='flex items-center gap-3'>
                <div className="bg-[#4338CA] p-2 rounded-lg text-white">
                    <Box size={22} />
                </div>
                <h3 className="text-xl font-extrabold text-[#111827]">Batch Performance</h3>
            </div>
        );

        const ModalSubtitle = () => (
            <div className='flex items-center gap-3 mt-1'>
                <span className='text-[13px] font-medium text-gray-500'>
                    {batches?.length ? `Showing ${batches.length} batches` : 'No batches found for the selected range'}
                </span>
                {selectedBatches?.length === 0 && batches?.length > 0 && (
                    <div className='flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full'>
                        <span className='w-1.5 h-1.5 bg-[#4338CA] rounded-full animate-ping' />
                        <span className='text-[10px] uppercase tracking-wider font-extrabold text-[#4338CA]'>
                            Please select a batch to proceed
                        </span>
                    </div>
                )}
            </div>
        );

        dispatch(modalActions.toggleCustomModal(true, {
            header: <ModalHeader />,
            subtitle: <ModalSubtitle />,
            onClose: () => {
                const startDate = lastFilters?.from || lastFilters?.dateRange?.from;
                const endDate = lastFilters?.to || lastFilters?.dateRange?.to;
                const dateOverride = (startDate && endDate) ? { from: moment(startDate).toDate(), to: moment(endDate).toDate() } : null;
                if (onCloseModal) onCloseModal(false, selectedBatches, dateOverride);
            },
            bodyContent: (
                <BatchPerformanceModalContent
                    batches={visibleBatches}
                    batchSource={batchSource}
                    onBatchSourceChange={handleBatchSourceChange}
                    initialFilters={activeFilters}
                    selectedBatch={selectedBatches}
                    setSelectedBatch={handleBatchSelect}
                    onSelectProductBatches={handleProductBatchesSelect}
                    onViewDetails={handleViewDetails}
                    onApply={(startDate, endDate) => handleFetchAndOpen({ from: startDate, to: endDate })}
                    isSingleSelect={isSingleSelect}
                    isSingleDate={isSingleDate}
                    shiftId={shiftId}
                    onShiftChange={onShiftChange}
                    maxDateRangeDays={maxDateRangeDays}
                    activeSelectAllOption={activeSelectAllOption}
                    customerId={customerId}
                    onClose={() => {
                        const startDate = lastFilters?.from || lastFilters?.dateRange?.from;
                        const endDate = lastFilters?.to || lastFilters?.dateRange?.to;
                        const dateOverride = (startDate && endDate) ? { from: moment(startDate).toDate(), to: moment(endDate).toDate() } : null;
                        if (onCloseModal) onCloseModal(false, selectedBatches, dateOverride);
                    }}
                />
            ),
            width: '50rem',
            height: '94vh',
            hideFooter: true,
            bodyClass: 'p-0',
            showCloseButton: true,
        }));
    };

    const handleFetchAndOpen = async (customFilters = null) => {
        let activeFilters = customFilters || filters;
        if (!customFilters && overrideDateRange?.from && overrideDateRange?.to) {
            activeFilters = {
                ...filters,
                dateRange: {
                    ...filters.dateRange,
                    from: overrideDateRange.from,
                    to: overrideDateRange.to,
                    active: true
                }
            };
        }

        setLastFilters(activeFilters);
        setSelectedBatches([]);
        try {
            const response = await aiPackaging.fetchBatchList({
                data: {
                    dateRangeFilter: {
                        dateType: activeFilters?.timeType?.value || 'scheduledAt',
                        from: moment(activeFilters?.dateRange?.from || activeFilters?.from).format('YYYY-MM-DD'),
                        to: moment(activeFilters?.dateRange?.to || activeFilters?.to).format('YYYY-MM-DD')
                    },
                }
            });

            const fetchedBatches = response?.data || response;
            const processedBatches = (fetchedBatches || []).map(b => ({
                ...b,
                grnItemBatchId: b.grnItemBatchId || `D-${b.productId}-${b.variantId}-${b.shiftId}-${b.costingDate}`
            }));
            setCurrentBatches(processedBatches);
        } catch (error) {
            console.error("Failed to fetch batch performance", error);
        }
    };

    return null;
};

export default BatchPerformanceHandler;